import { authApi } from '@/features/auth/api/authApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useThemeStore } from '../providers/ThemeProvider';
import { useStudentAuthStore } from '@/features/auth/store/authStore';
import { AuthExpiredDialog } from '../AuthExpiredDialog';
import { STUDENT_LOGO_SRC } from '@/features/shared/brand';
import { usePracticeShellStore } from '@/features/practice/store/practiceShellStore';
import { PracticeQuestionNavigationTree } from '@/features/practice/components/PracticeQuestionNavigationTree';
import practiceApi from '@/features/practice/api/practiceApi';
import type { PracticeQuestionCatalogItem, PracticeQuestionNavigationContext } from '@/features/practice/types/practice';
import { buildPracticeNavigationSections, reindexCurrentTypeNavigation } from '@/features/practice/utils/navigationTree';
import type { StudentAnnouncement } from '@/features/auth/api/authApi';
import { StudentLocaleProvider, studentText } from '../providers/StudentLocaleProvider';
import { initializeStudentTelemetry, trackStudentEvent } from '@/features/analytics/studentTelemetry';
import { resolveStudentThemeStyle, type StudentShellThemeStyle } from '../theme/StudentThemeBoundary';

const navItems = [
  { to: '/lobby', label: 'Practice Hub', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4' },
  { to: '/learning', label: 'Vocabulary Learning', icon: 'M4 5.5A2.5 2.5 0 016.5 3H20v15H6.5A2.5 2.5 0 014 15.5zM4 5.5v13A2.5 2.5 0 006.5 21H20' },
  { to: '/rankings', label: 'Leaderboard', icon: 'M8 21h8M12 17v4M7 4h10M8 4v5a4 4 0 008 0V4M5 4v5a7 7 0 0014 0V4' },
  { to: '/profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
];
const EMPTY_ANNOUNCEMENTS: StudentAnnouncement[] = [];
const COMPACT_SIDEBAR_MEDIA_QUERY = '(max-width: 1199px)';
const MIN_SIDEBAR_LOGO_WIDTH_PX = 32;
const MIN_SIDEBAR_LOGO_HEIGHT_PX = 16;
const MAX_SIDEBAR_LOGO_DIMENSION_PX = 4096;
const MAX_SIDEBAR_LOGO_PIXELS = 8_000_000;
const MIN_SIDEBAR_LOGO_ASPECT_RATIO = 0.5;
const MAX_SIDEBAR_LOGO_ASPECT_RATIO = 8;

function isSuitableSidebarLogoImage(image: HTMLImageElement) {
  const { naturalWidth: width, naturalHeight: height } = image;
  const aspectRatio = width / height;
  return Number.isFinite(aspectRatio)
    && width >= MIN_SIDEBAR_LOGO_WIDTH_PX
    && height >= MIN_SIDEBAR_LOGO_HEIGHT_PX
    && width <= MAX_SIDEBAR_LOGO_DIMENSION_PX
    && height <= MAX_SIDEBAR_LOGO_DIMENSION_PX
    && width * height <= MAX_SIDEBAR_LOGO_PIXELS
    && aspectRatio >= MIN_SIDEBAR_LOGO_ASPECT_RATIO
    && aspectRatio <= MAX_SIDEBAR_LOGO_ASPECT_RATIO;
}

function isSafeAnnouncementUrl(value: string) {
  if (value.startsWith('/')) return true;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  ));

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, [query]);

  return matches;
}

function IconTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="group relative inline-flex">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-[220px] -translate-x-1/2 whitespace-normal rounded-[var(--radius-sm)] bg-[var(--color-text)] px-2 py-1 text-center text-[11px] font-medium leading-4 text-[var(--color-text-inverse)] opacity-0 shadow-[var(--shadow-sm)] transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        {label}
      </span>
    </span>
  );
}

function QuestionNavigationSkeleton({ locale }: { locale: 'zh-CN' | 'en-US' }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-4" aria-label={studentText(locale, 'Loading question navigation', '正在加载题目导航')}>
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] px-1 pb-3">
        <div className="h-4 w-28 animate-pulse rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
        <div className="ml-auto h-5 w-20 animate-pulse rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
        <div className="h-7 w-16 animate-pulse rounded-[var(--radius-sm)] bg-[var(--color-surface-muted)]" />
      </div>
      <div className="student-scrollbar min-h-0 flex-1 overflow-y-auto pt-4">
        <div className="space-y-5">
        {['Speaking', 'Writing', 'Reading', 'Listening'].map((section) => (
          <div key={section} className="space-y-3 border-b border-[var(--color-border)] pb-4 last:border-b-0">
            <div className="flex items-center justify-between gap-3">
              <div className="h-3 w-24 animate-pulse rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
              <div className="h-5 w-24 animate-pulse rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
            </div>
            <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="h-4 w-24 animate-pulse rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
                <div className="h-5 w-20 animate-pulse rounded-[var(--radius-full)] bg-[var(--color-surface-muted)]" />
              </div>
              <div className="space-y-2">
                <div className="h-12 animate-pulse rounded-[var(--radius-md)] bg-[var(--color-surface-muted)]" />
                <div className="h-12 animate-pulse rounded-[var(--radius-md)] bg-[var(--color-surface-muted)]" />
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

export function StudentLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useThemeStore();
  const queryClient = useQueryClient();
  const clearSession = useStudentAuthStore((state) => state.clearSession);
  const isCompactSidebarViewport = useMediaQuery(COMPACT_SIDEBAR_MEDIA_QUERY);
  const [wechatPreviewOpen, setWechatPreviewOpen] = useState(false);
  const [wechatModalOpen, setWechatModalOpen] = useState(false);
  const [customContactDialogOpen, setCustomContactDialogOpen] = useState(false);
  const [failedSidebarLogoSrc, setFailedSidebarLogoSrc] = useState<string | null>(null);
  const [loadedSidebarLogoSrc, setLoadedSidebarLogoSrc] = useState<string | null>(null);
  const [compactSidebarOpen, setCompactSidebarOpen] = useState(false);
  const [practiceSidebarCollapsedState, setPracticeSidebarCollapsedState] = useState<boolean | null>(null);
  const [sidebarLibraryTypeCode, setSidebarLibraryTypeCode] = useState<string | null>(null);
  const [sidebarLibraryPage, setSidebarLibraryPage] = useState(1);
  const [sidebarLibraryItems, setSidebarLibraryItems] = useState<PracticeQuestionCatalogItem[]>([]);
  const [sidebarLibraryTotal, setSidebarLibraryTotal] = useState(0);
  const [hiddenAnnouncementIds, setHiddenAnnouncementIds] = useState<string[]>([]);
  const practiceNavigationVisible = usePracticeShellStore((state) => state.navigationVisible);
  const practiceNavigationDrawerLabel = usePracticeShellStore((state) => state.drawerLabel);
  const practiceNavigationCurrentQuestionId = usePracticeShellStore((state) => state.currentQuestionId);
  const practiceNavigationCurrentIndex = usePracticeShellStore((state) => state.currentIndex);
  const practiceNavigationCurrentTotal = usePracticeShellStore((state) => state.totalCount);
  const practiceNavigationSidebarSections = usePracticeShellStore((state) => state.sidebarSections);
  const practiceNavigationDrawerSections = usePracticeShellStore((state) => state.drawerSections);
  const practiceNavigationSelect = usePracticeShellStore((state) => state.onSelectQuestion);
  const practiceNavigationLoadMore = usePracticeShellStore((state) => state.onLoadMore);
  const practiceNavigationLoadingMore = usePracticeShellStore((state) => state.navigationLoadingMore);
  const practiceNavigationFilterEnabled = usePracticeShellStore((state) => state.navigationFilterEnabled);
  const practiceNavigationFilter = usePracticeShellStore((state) => state.navigationFilter);
  const practiceNavigationFilterShowFrequency = usePracticeShellStore((state) => state.navigationFilterShowFrequency);
  const practiceNavigationFilterShowNew = usePracticeShellStore((state) => state.navigationFilterShowNew);
  const practiceNavigationFilterChange = usePracticeShellStore((state) => state.onNavigationFilterChange);
  const practiceNavigationDrawerOpen = usePracticeShellStore((state) => state.drawerOpen);
  const practiceNavigationExpansion = usePracticeShellStore((state) => state.navigationExpansion);
  const setPracticeNavigationDrawerOpen = usePracticeShellStore((state) => state.setDrawerOpen);
  const {
    data: shellSettings = null,
    isFetched: shellSettingsFetched,
    isError: shellSettingsIsError,
  } = useQuery({
    queryKey: ['student-shell-settings'],
    queryFn: ({ signal }) => authApi.getShellSettings({ signal }),
    staleTime: 60_000,
  });
  const shellSettingsResolved = shellSettingsFetched || shellSettingsIsError;
  const isPracticeRoute = location.pathname.startsWith('/practice/');
  const practiceSearchParams = new URLSearchParams(location.search);
  const prioritizeHighFrequency = isPracticeRoute
    && !practiceSearchParams.get('questionSetId')
    && ['1', 'true'].includes(practiceSearchParams.get('prioritizeHighFrequency') || '');
  useEffect(() => {
    setCompactSidebarOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!isCompactSidebarViewport || !compactSidebarOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [compactSidebarOpen, isCompactSidebarViewport]);
  useEffect(() => {
    if (!compactSidebarOpen && !customContactDialogOpen) {
      return;
    }
    const closeTopLayer = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (customContactDialogOpen) {
        setCustomContactDialogOpen(false);
        return;
      }
      setCompactSidebarOpen(false);
    };
    document.addEventListener('keydown', closeTopLayer);
    return () => document.removeEventListener('keydown', closeTopLayer);
  }, [compactSidebarOpen, customContactDialogOpen]);
  useEffect(() => {
    initializeStudentTelemetry();
    trackStudentEvent('route_viewed', { route: `${location.pathname}${location.search}` });
  }, [location.pathname, location.search]);
  const currentAnnouncementQuestionType = useMemo(() => {
    for (const section of practiceNavigationDrawerSections) {
      const currentGroup = section.typeGroups.find((group) => group.isCurrentType);
      if (currentGroup) {
        return currentGroup.questionTypeCode;
      }
    }
    return undefined;
  }, [practiceNavigationDrawerSections]);
  const { data: announcementsData } = useQuery<StudentAnnouncement[]>({
    queryKey: ['announcements', currentAnnouncementQuestionType || 'all'],
    queryFn: ({ signal }) => authApi.getActiveAnnouncements(currentAnnouncementQuestionType, { signal }),
    // Practice pages render their navigation and do not show the global
    // announcement surface. Defer this request until a non-practice route.
    enabled: shellSettingsResolved && !isPracticeRoute,
    staleTime: 45_000,
    refetchInterval: 60_000,
  });
  const announcements = announcementsData ?? EMPTY_ANNOUNCEMENTS;
  const activeAnnouncement = useMemo(
    () => announcements.find((item) => !hiddenAnnouncementIds.includes(item.id)) || null,
    [announcements, hiddenAnnouncementIds],
  );
  const practiceSidebarCollapsible = Boolean(shellSettings?.practiceSidebarCollapsible ?? true);
  useEffect(() => {
    if (!isPracticeRoute || !shellSettingsResolved) {
      setPracticeSidebarCollapsedState(false);
      return;
    }
    setPracticeSidebarCollapsedState(Boolean(
      practiceSidebarCollapsible && shellSettings?.practiceSidebarCollapsed,
    ));
  }, [isPracticeRoute, practiceSidebarCollapsible, shellSettings?.practiceSidebarCollapsed, shellSettingsResolved]);
  const questionNavigationAllowed = Boolean(
    shellSettingsResolved
    && isPracticeRoute
    && (shellSettings?.showQuestionNavigation ?? true),
  );
  const questionNavigationEnabled = Boolean(
    questionNavigationAllowed
    && practiceNavigationVisible
    && practiceNavigationSidebarSections.length
    && practiceNavigationSelect,
  );
  const showHighFrequencyNavigationBadge = Boolean(
    shellSettings?.showHighFrequencyQuestionBadge !== false
    && shellSettings?.showHighFrequencyNavigationBadge !== false,
  );
  const questionNavigationLoading = Boolean(
    isPracticeRoute
    && !questionNavigationEnabled
    && !shellSettingsIsError
    && (
      !shellSettingsResolved
      || (shellSettings?.showQuestionNavigation ?? true)
    ),
  );
  const sidebarLibraryCurrentQuestionId = currentAnnouncementQuestionType === sidebarLibraryTypeCode
    ? practiceNavigationCurrentQuestionId || undefined
    : undefined;
  const {
    data: sidebarLibraryPageData,
    isFetching: sidebarLibraryFetching,
  } = useQuery({
    queryKey: ['question-navigation-items', 'sidebar-library', sidebarLibraryTypeCode, sidebarLibraryPage, 100, sidebarLibraryCurrentQuestionId, prioritizeHighFrequency],
    queryFn: async ({ signal }) => ({
      questionTypeCode: sidebarLibraryTypeCode!,
      page: await practiceApi.getQuestionTypeCatalog(
        sidebarLibraryTypeCode!,
        {
          page: sidebarLibraryPage,
          pageSize: 100,
          currentQuestionId: sidebarLibraryCurrentQuestionId,
          prioritizeHighFrequency,
        },
        { signal },
      ),
    }),
    enabled: Boolean(questionNavigationEnabled && sidebarLibraryTypeCode),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!sidebarLibraryPageData || sidebarLibraryPageData.questionTypeCode !== sidebarLibraryTypeCode) {
      return;
    }
    setSidebarLibraryTotal(sidebarLibraryPageData.page.total);
    setSidebarLibraryItems((current) => {
      const next = sidebarLibraryPage === 1 ? [] : [...current];
      const seen = new Set(next.map((item) => item.questionId));
      sidebarLibraryPageData.page.items.forEach((item) => {
        if (!seen.has(item.questionId)) {
          next.push(item);
          seen.add(item.questionId);
        }
      });
      return next;
    });
  }, [sidebarLibraryPage, sidebarLibraryPageData, sidebarLibraryTypeCode]);
  const sidebarLibraryGroupMeta = useMemo(
    () => practiceNavigationSidebarSections
      .flatMap((section) => section.typeGroups.map((group) => ({ section, group })))
      .find(({ group }) => group.questionTypeCode === sidebarLibraryTypeCode) || null,
    [practiceNavigationSidebarSections, sidebarLibraryTypeCode],
  );
  const sidebarLibrarySections = useMemo(() => {
    if (!sidebarLibraryTypeCode || !sidebarLibraryItems.length) {
      return [];
    }
    const context: PracticeQuestionNavigationContext = {
      contextType: 'catalog',
      questionTypeCode: sidebarLibraryTypeCode,
      currentQuestionId: sidebarLibraryCurrentQuestionId || '',
      currentIndex: Math.max(0, sidebarLibraryItems.findIndex((item) => item.questionId === sidebarLibraryCurrentQuestionId)),
      totalCount: sidebarLibraryTotal,
      items: sidebarLibraryItems.map((item) => ({
        ...item,
        status: 'published',
        isCurrent: item.questionId === sidebarLibraryCurrentQuestionId,
      })),
    };
    const sections = buildPracticeNavigationSections(context, sidebarLibraryCurrentQuestionId ? sidebarLibraryTypeCode : null)
      .map((section) => ({
        ...section,
        sectionLabel: sidebarLibraryGroupMeta?.section.sectionLabel || section.sectionLabel,
        totalCount: sidebarLibraryTotal,
        completedCount: sidebarLibraryGroupMeta?.group.completedCount ?? section.completedCount,
        typeGroups: section.typeGroups.map((group) => ({
          ...group,
          typeName: sidebarLibraryGroupMeta?.group.typeName || group.typeName,
          totalCount: sidebarLibraryTotal,
          completedCount: sidebarLibraryGroupMeta?.group.completedCount ?? group.completedCount,
        })),
      }));
    return reindexCurrentTypeNavigation(
      sections,
      sidebarLibraryCurrentQuestionId,
      practiceNavigationCurrentIndex,
    );
  }, [practiceNavigationCurrentIndex, sidebarLibraryCurrentQuestionId, sidebarLibraryGroupMeta, sidebarLibraryItems, sidebarLibraryTotal, sidebarLibraryTypeCode]);
  const studentLocale = isPracticeRoute && (shellSettings?.practiceUiLanguageIndependent ?? true)
    ? 'en-US'
    : (shellSettings?.studentUiLanguage || 'zh-CN');
  const configuredPracticeSidebarWidth = Math.min(480, Math.max(240, shellSettings?.practiceSidebarWidthPx ?? 380));
  const desktopPracticeSidebarCollapsed = isPracticeRoute && practiceSidebarCollapsible
    ? (practiceSidebarCollapsedState ?? Boolean(shellSettings?.practiceSidebarCollapsed))
    : false;
  const practiceSidebarCollapsed = !isCompactSidebarViewport && desktopPracticeSidebarCollapsed;
  const desktopSidebarWidth = isPracticeRoute
    ? (practiceSidebarCollapsed ? 0 : configuredPracticeSidebarWidth)
    : 240;
  const sidebarOpen = isCompactSidebarViewport ? compactSidebarOpen : !practiceSidebarCollapsed;
  const layoutStyle: StudentShellThemeStyle = {
    '--student-shell-sidebar-width': isCompactSidebarViewport ? '0px' : `${desktopSidebarWidth}px`,
    '--student-shell-sidebar-panel-width': isCompactSidebarViewport
      ? 'min(88vw, 360px)'
      : `${desktopSidebarWidth}px`,
  };
  const shellThemeStyle = resolveStudentThemeStyle(shellSettings, theme, isPracticeRoute, layoutStyle);
  const configuredSidebarLogoSrc = shellSettings?.themePreset === 'archive_home'
    ? shellSettings.prototypeSidebarLogoUrl || STUDENT_LOGO_SRC
    : STUDENT_LOGO_SRC;
  const sidebarLogoSrc = configuredSidebarLogoSrc === failedSidebarLogoSrc
    ? STUDENT_LOGO_SRC
    : configuredSidebarLogoSrc;
  const customSidebarLogoActive = sidebarLogoSrc !== STUDENT_LOGO_SRC;
  const sidebarLogoHeightPx = Math.min(72, Math.max(24, shellSettings?.studentLogoHeightPx ?? 32));
  const showContactEntry = Boolean(
    shellSettings
    && (
      (shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl)
      || shellSettings.contactEmail
      || shellSettings.contactWechatId
      || shellSettings.contactWechatAddress
      || shellSettings.contactExternalUrl
      || (shellSettings.contactCustomEnabled && shellSettings.contactCustomIconUrl)
    ),
  );
  const contactUsesSocialIcons = shellSettings?.contactDisplayMode === 'social_icons'
    || shellSettings?.contactDisplayMode === 'single_qr_social_icons';
  const contactUsesCombinedMode = shellSettings?.contactDisplayMode === 'single_qr_social_icons';
  const showWechatSocialIcon = Boolean(
    contactUsesSocialIcons
    && shellSettings
    && (
      contactUsesCombinedMode
        ? (shellSettings.contactWechatId || shellSettings.contactWechatAddress)
        : ((shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl) || shellSettings.contactWechatAddress)
    ),
  );
  const compactSidebarInertProps = isCompactSidebarViewport && !compactSidebarOpen
    ? ({ inert: '' } as Record<string, string>)
    : {};
  useEffect(() => {
    if (!showContactEntry || !sidebarOpen || !shellSettings?.contactWechatQrUrl) return;
    if (shellSettings.contactDisplayMode !== 'social_icons') {
      trackStudentEvent('contact_qr_exposed', { properties: { source: 'single_qr' } });
    }
  }, [shellSettings?.contactDisplayMode, shellSettings?.contactWechatQrUrl, showContactEntry, sidebarOpen]);
  useEffect(() => {
    if (wechatPreviewOpen) {
      trackStudentEvent('contact_qr_exposed', { properties: { source: 'social_icons' } });
    }
  }, [wechatPreviewOpen]);

  const copyContact = async () => {
    const value = shellSettings?.contactWechatId;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      trackStudentEvent('contact_copied', { properties: { source: 'student_entry' } });
      window.dispatchEvent(new CustomEvent('pps-contact-copied'));
    } catch {
      trackStudentEvent('ui_error', { properties: { area: 'contact_copy' } });
    }
  };
  const currentTypeNavigationMeta = useMemo(() => {
    if (practiceNavigationCurrentIndex > 0) {
      return {
        currentIndex: practiceNavigationCurrentIndex,
        totalCount: practiceNavigationCurrentTotal,
      };
    }
    let totalCount = 0;
    for (const section of practiceNavigationDrawerSections) {
      for (const group of section.typeGroups) {
        totalCount = Math.max(totalCount, group.totalCount);
        const currentItemIndex = group.items.findIndex((item) => item.isCurrent);
        if (currentItemIndex >= 0) {
          return {
            currentIndex: currentItemIndex + 1,
            totalCount: group.items.length,
          };
        }
      }
    }
    return {
      currentIndex: 0,
      totalCount,
    };
  }, [practiceNavigationCurrentIndex, practiceNavigationCurrentTotal, practiceNavigationDrawerSections]);

  const handlePracticeQuestionSelect = (questionId: string) => {
    setPracticeNavigationDrawerOpen(false);
    setSidebarLibraryTypeCode(null);
    setCompactSidebarOpen(false);
    practiceNavigationSelect?.(questionId);
  };

  const openSidebarLibrary = (questionTypeCode: string) => {
    setCompactSidebarOpen(false);
    setSidebarLibraryItems([]);
    setSidebarLibraryTotal(0);
    setSidebarLibraryPage(1);
    setSidebarLibraryTypeCode(questionTypeCode);
    trackStudentEvent('navigation_opened', {
      properties: { source: 'sidebar_type', questionTypeCode },
    });
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout failures and clear local session to avoid trapping the user in a bad state.
    } finally {
      clearSession();
      navigate('/login');
    }
  };

  const dismissAnnouncement = async (announcement: StudentAnnouncement) => {
    setHiddenAnnouncementIds((current) => [...current, announcement.id]);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(`pps:announcement:${announcement.id}`, '1');
    }
    try {
      await authApi.dismissAnnouncement(announcement.id);
      await queryClient.invalidateQueries({ queryKey: ['announcements'] });
    } catch {
      // A local dismissal still prevents a blocking repeat when the network is unavailable.
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const nextHiddenIds = announcements
      .filter((item) => window.sessionStorage.getItem(`pps:announcement:${item.id}`) === '1')
      .map((item) => item.id);
    setHiddenAnnouncementIds((current) => (
      current.length === nextHiddenIds.length
      && current.every((item, index) => item === nextHiddenIds[index])
        ? current
        : nextHiddenIds
    ));
  }, [announcements]);

  if (!shellSettingsResolved) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]" role="status" aria-label="Loading">
        <span className="h-8 w-8 animate-spin rounded-[var(--radius-full)] border-2 border-[var(--color-border)] border-t-[var(--color-primary)]" aria-hidden="true" />
      </div>
    );
  }

  return (
    <StudentLocaleProvider locale={studentLocale as 'zh-CN' | 'en-US'}>
    <div className={`student-shell-theme flex min-h-screen min-w-0 overflow-x-clip bg-[var(--color-bg)] ${isPracticeRoute ? 'student-practice-scope' : ''}`} style={shellThemeStyle}>
      <AuthExpiredDialog />
      {activeAnnouncement && !isPracticeRoute ? (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-[color:var(--color-overlay-scrim)] p-5">
          <section className="w-full max-w-[520px] overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] shadow-[var(--shadow-lg)]" role="dialog" aria-modal="true" aria-labelledby="student-announcement-title">
              <div className={`border-b border-[var(--color-border)] px-5 py-4 ${activeAnnouncement.severity === 'critical' ? 'bg-[var(--color-danger-light)]' : activeAnnouncement.severity === 'warning' ? 'bg-[var(--color-warning-light)]' : 'bg-[var(--color-primary-light)]'}`}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">{activeAnnouncement.kind === 'maintenance' ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Maintenance', '维护通知') : activeAnnouncement.kind === 'update' ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Product update', '产品更新') : studentText(studentLocale as 'zh-CN' | 'en-US', 'Announcement', '公告')}</div>
              <h2 id="student-announcement-title" className="mt-1 text-lg font-semibold text-[var(--color-text)]">{activeAnnouncement.title}</h2>
              {activeAnnouncement.summary ? <p className="mt-1 text-sm leading-5 text-[var(--color-text-secondary)]">{activeAnnouncement.summary}</p> : null}
            </div>
            <div className="px-5 py-5">
              <p className="whitespace-pre-wrap text-sm leading-6 text-[var(--color-text)]">{activeAnnouncement.body}</p>
              <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                {activeAnnouncement.actionUrl && activeAnnouncement.actionLabel && isSafeAnnouncementUrl(activeAnnouncement.actionUrl) ? (
                  <a href={activeAnnouncement.actionUrl} target={activeAnnouncement.actionUrl.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="inline-flex h-9 items-center rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]">{activeAnnouncement.actionLabel}</a>
                ) : null}
                {activeAnnouncement.dismissible || activeAnnouncement.displayMode !== 'immediate' ? (
                  <button type="button" onClick={() => void dismissAnnouncement(activeAnnouncement)} className="inline-flex h-9 items-center rounded-[var(--radius-sm)] bg-[var(--color-primary)] px-3 text-sm font-semibold text-[var(--color-text-inverse)] hover:bg-[var(--color-primary-hover)]">{studentText(studentLocale as 'zh-CN' | 'en-US', 'Got it', '知道了')}</button>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      ) : null}
      {isCompactSidebarViewport && compactSidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-[color:var(--color-overlay-scrim)]"
          onClick={() => setCompactSidebarOpen(false)}
          aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Close navigation sidebar', '关闭导航侧栏')}
        />
      ) : null}
      <aside
        {...compactSidebarInertProps}
        className={`fixed inset-y-0 left-0 z-50 flex h-dvh min-w-0 flex-col overflow-hidden border-r border-[var(--color-border)] bg-[var(--color-shell-sidebar-bg)] text-[var(--color-shell-sidebar-foreground)] shadow-[var(--shadow-sm)] transition-[width,transform] duration-200 ${isCompactSidebarViewport && !compactSidebarOpen ? '-translate-x-full' : 'translate-x-0'}`}
        aria-hidden={isCompactSidebarViewport && !compactSidebarOpen}
        style={{
          width: 'var(--student-shell-sidebar-panel-width)',
          '--color-surface': 'var(--color-shell-sidebar-bg)',
          '--color-surface-raised': 'var(--color-shell-sidebar-bg)',
          '--color-surface-muted': 'color-mix(in srgb, var(--color-shell-sidebar-foreground) 7%, var(--color-shell-sidebar-bg))',
          '--color-text': 'var(--color-shell-sidebar-foreground)',
          '--color-text-secondary': 'color-mix(in srgb, var(--color-shell-sidebar-foreground) 68%, var(--color-shell-sidebar-bg))',
          '--color-text-tertiary': 'color-mix(in srgb, var(--color-shell-sidebar-foreground) 48%, var(--color-shell-sidebar-bg))',
          '--color-border': 'color-mix(in srgb, var(--color-shell-sidebar-foreground) 18%, var(--color-shell-sidebar-bg))',
        } as StudentShellThemeStyle}
      >
        <div className="shrink-0 border-b border-[var(--color-border)]">
          {!practiceSidebarCollapsed ? <div className="flex min-w-0 items-center justify-center px-5 py-5">
            <div
              className="flex items-center justify-center"
              style={{
                width: '80%',
                height: `${sidebarLogoHeightPx}px`,
                minWidth: 0,
                maxWidth: 180,
                overflow: 'hidden',
              }}
            >
              <img
                key={sidebarLogoSrc}
                src={sidebarLogoSrc}
                alt={shellSettings?.studentSiteTitle || 'PTE Practice'}
                data-sidebar-logo-source={customSidebarLogoActive ? 'custom' : 'default'}
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  visibility: customSidebarLogoActive && loadedSidebarLogoSrc !== sidebarLogoSrc ? 'hidden' : 'visible',
                }}
                onLoad={(event) => {
                  if (!customSidebarLogoActive) return;
                  if (isSuitableSidebarLogoImage(event.currentTarget)) {
                    setLoadedSidebarLogoSrc(sidebarLogoSrc);
                  } else {
                    setFailedSidebarLogoSrc(configuredSidebarLogoSrc);
                  }
                }}
                onError={() => {
                  if (customSidebarLogoActive) setFailedSidebarLogoSrc(configuredSidebarLogoSrc);
                }}
              />
            </div>
          </div> : null}
          {!practiceSidebarCollapsed ? <div className="px-5 py-2.5 text-center">
            <div className="truncate font-display text-[15px] font-bold text-[var(--color-text)]">
              {shellSettings?.studentSiteTitle || studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice', '练习')}
            </div>
          </div> : null}
        </div>
        {questionNavigationEnabled && !practiceSidebarCollapsed ? (
          <div className={`min-h-0 flex-1 px-4 py-4 ${shellSettings?.showSidebarNavigationHeader === false ? 'student-scrollbar overflow-y-auto' : 'overflow-hidden'}`}>
            <PracticeQuestionNavigationTree
              sections={practiceNavigationSidebarSections}
              onSelectQuestion={handlePracticeQuestionSelect}
              onOpenType={openSidebarLibrary}
              layoutVariant="sidebar"
              expansionMode={practiceNavigationExpansion}
              scrollToCurrentSignal={location.pathname}
              showNavigationHeader={shellSettings?.showSidebarNavigationHeader !== false}
              navigationTitle={studentText(studentLocale as 'zh-CN' | 'en-US', 'Questions', '题目')}
              typeItemHeightPx={shellSettings?.sidebarTypeItemHeightPx ?? 40}
              typeFontSizePx={Math.max(11, shellSettings?.sidebarTypeFontSizePx ?? 14)}
              typeItemGapPx={shellSettings?.sidebarTypeItemGapPx ?? 12}
              showSectionBorder={shellSettings?.showSidebarSectionBorder !== false}
              showTypeBorder={shellSettings?.showSidebarTypeBorder !== false}
              showQuestionBorder={shellSettings?.showSidebarQuestionBorder === true}
              showTypeSelectedColor={shellSettings?.showSidebarTypeSelectedColor !== false}
              showHighFrequencyBadge={showHighFrequencyNavigationBadge}
              questionBadgeStyle={shellSettings?.questionBadgeStyle ?? 'soft'}
              onLoadMore={practiceNavigationLoadMore || undefined}
              loadingMore={practiceNavigationLoadingMore}
            />
          </div>
        ) : questionNavigationLoading && !practiceSidebarCollapsed ? (
          <QuestionNavigationSkeleton locale={studentLocale as 'zh-CN' | 'en-US'} />
        ) : !practiceSidebarCollapsed ? (
          <nav aria-label="Primary navigation" className="student-scrollbar min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
            {navItems.filter((item) => (
              item.to !== '/learning' || shellSettings?.capabilities?.learning
            ) && (
              item.to !== '/rankings' || shellSettings?.capabilities?.leaderboard
            )).map(item => (
              <NavLink key={item.to} to={item.to}
                onClick={() => setCompactSidebarOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)] font-semibold' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]'}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={item.icon} /></svg>
                {item.to === '/lobby'
                  ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice Hub', '练习大厅')
                  : item.to === '/learning'
                    ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Vocabulary Learning', '词汇学习')
                    : item.to === '/rankings'
                      ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Leaderboard', '排行榜')
                      : studentText(studentLocale as 'zh-CN' | 'en-US', 'Profile', '个人中心')}
              </NavLink>
            ))}
          </nav>
        ) : null}
        <div
          className={`shrink-0 border-t border-[var(--color-border)] ${practiceSidebarCollapsed ? 'p-3' : 'space-y-2 px-4 pt-3'}`}
          style={{ paddingBottom: 'max(var(--space-md), env(safe-area-inset-bottom))' }}
        >
          {showContactEntry && shellSettings && !practiceSidebarCollapsed && shellSettings.contactDisplayMode !== 'social_icons' && shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl ? (
            <div className="flex flex-col items-center gap-2 pb-1 text-center">
              <button
                type="button"
                onClick={() => { trackStudentEvent('contact_qr_clicked', { properties: { source: 'single_qr' } }); setWechatModalOpen(true); }}
                className="group rounded-[var(--radius-md)] p-1 transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]"
                aria-label={shellSettings.contactWechatTitle || studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact support', '联系教务')}
                title={shellSettings.contactWechatTitle || studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact support', '联系教务')}
              >
                <img
                  src={shellSettings.contactWechatQrUrl}
                  alt={shellSettings.contactWechatTitle || 'WeChat QR code'}
                  className={`block rounded-[var(--radius-sm)] object-contain transition-transform duration-200 group-hover:scale-[1.03] ${isCompactSidebarViewport ? 'h-[72px] w-[72px]' : 'h-24 w-24'}`}
                />
              </button>
              {shellSettings.contactWechatHint ? (
                <p className="line-clamp-2 max-w-[220px] text-xs leading-5 text-[var(--color-text-secondary)]">
                  {shellSettings.contactWechatHint}
                </p>
              ) : null}
            </div>
          ) : null}
          {showContactEntry && shellSettings && !practiceSidebarCollapsed && !contactUsesSocialIcons && shellSettings.contactWechatId ? (
            <button
              type="button"
              onClick={() => { void copyContact(); }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
              {studentText(studentLocale as 'zh-CN' | 'en-US', 'Copy WeChat ID', '复制微信号')}
            </button>
          ) : null}
          {showContactEntry && shellSettings && !practiceSidebarCollapsed && !contactUsesSocialIcons && shellSettings.contactWechatAddress ? (
            <a
              href={shellSettings.contactWechatAddress}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-full items-center justify-center rounded-[var(--radius-sm)] px-3 py-2 text-xs font-semibold text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"
            >
              {studentText(studentLocale as 'zh-CN' | 'en-US', 'Open WeChat contact', '打开微信联系地址')}
            </a>
          ) : null}
          {showContactEntry && shellSettings && !practiceSidebarCollapsed && (contactUsesSocialIcons || (shellSettings.contactCustomEnabled && shellSettings.contactCustomIconUrl)) ? (
            <div className="flex w-full flex-wrap items-center justify-center gap-2 border-b border-[var(--color-border)] pb-3" aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact and sign out', '联系与退出')}>
              {showWechatSocialIcon ? (
                <div
                  className="relative"
                  onMouseEnter={() => {
                    if (!contactUsesCombinedMode && shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl) {
                      setWechatPreviewOpen(true);
                    }
                  }}
                  onMouseLeave={() => setWechatPreviewOpen(false)}
                >
                  <button type="button" aria-label="WeChat" title="WeChat" onClick={() => {
                    if (!shellSettings.contactWechatId && shellSettings.contactWechatAddress) {
                      trackStudentEvent('contact_external_clicked', { properties: { source: 'wechat_icon' } });
                      window.open(shellSettings.contactWechatAddress, '_blank', 'noopener,noreferrer');
                    } else if (shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl) {
                      trackStudentEvent('contact_qr_clicked', { properties: { source: 'social_icons' } });
                      setWechatModalOpen(true);
                    } else if (shellSettings.contactWechatAddress) {
                      window.open(shellSettings.contactWechatAddress, '_blank', 'noopener,noreferrer');
                    }
                  }} className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-success-light)] hover:text-[var(--color-success)]">
                    {shellSettings.contactWechatIconUrl ? <img src={shellSettings.contactWechatIconUrl} alt="" className="h-5 w-5 object-contain" /> : <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M8 12h.01M12 12h.01M16 12h.01" /><path d="M21 12c0 4.97-4.03 9-9 9-1.48 0-2.88-.36-4.12-.99L3 21l1.11-4.44A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9s9 4.03 9 9Z" /></svg>}
                  </button>
                  {wechatPreviewOpen && !contactUsesCombinedMode && shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl ? <div className="absolute bottom-full left-1/2 z-10 mb-2 w-[180px] -translate-x-1/2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-2 shadow-[var(--shadow-md)]"><img src={shellSettings.contactWechatQrUrl} alt="WeChat QR code" className="block w-full rounded-[var(--radius-sm)]" /></div> : null}
                </div>
              ) : null}
              {contactUsesSocialIcons && shellSettings.contactExternalUrl ? <a href={shellSettings.contactExternalUrl} target="_blank" rel="noreferrer" aria-label={shellSettings.contactExternalLabel || 'External contact'} title={shellSettings.contactExternalLabel || 'External contact'} onClick={() => trackStudentEvent('contact_external_clicked', { properties: { source: 'social_icons' } })} className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-danger-light)] hover:text-[var(--color-danger)]">{shellSettings.contactExternalIconUrl ? <img src={shellSettings.contactExternalIconUrl} alt="" className="h-5 w-5 object-contain" /> : <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="4" /><path d="M8 9h8M8 12h5M8 15h8" /></svg>}</a> : null}
              {contactUsesSocialIcons && shellSettings.contactEmail ? <a href={`mailto:${shellSettings.contactEmail}`} aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Email', '邮件')} title={studentText(studentLocale as 'zh-CN' | 'en-US', 'Email', '邮件')} onClick={() => trackStudentEvent('contact_email_clicked', { properties: { source: 'social_icons' } })} className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)]">{shellSettings.contactEmailIconUrl ? <img src={shellSettings.contactEmailIconUrl} alt="" className="h-5 w-5 object-contain" /> : <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" /></svg>}</a> : null}
              {contactUsesSocialIcons && !contactUsesCombinedMode && shellSettings.contactWechatId ? <button type="button" onClick={() => { void copyContact(); }} aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Copy WeChat ID', '复制微信号')} title={studentText(studentLocale as 'zh-CN' | 'en-US', 'Copy WeChat ID', '复制微信号')} className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg></button> : null}
              {shellSettings.contactCustomEnabled && shellSettings.contactCustomIconUrl ? (
                <IconTooltip label={shellSettings.contactCustomTooltip || studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice guide', '练习方式')}>
                  {shellSettings.contactCustomActionMode === 'link' && shellSettings.contactCustomLinkUrl ? (
                    <a
                      href={shellSettings.contactCustomLinkUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={shellSettings.contactCustomTooltip || studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice guide', '练习方式')}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)]"
                      onClick={() => trackStudentEvent('contact_external_clicked', { properties: { source: 'custom_practice_method' } })}
                    >
                      <img src={shellSettings.contactCustomIconUrl} alt="" className="h-5 w-5 object-contain" />
                    </a>
                  ) : (
                    <button
                      type="button"
                      aria-label={shellSettings.contactCustomTooltip || studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice guide', '练习方式')}
                      className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)]"
                      onClick={() => setCustomContactDialogOpen(true)}
                    >
                      <img src={shellSettings.contactCustomIconUrl} alt="" className="h-5 w-5 object-contain" />
                    </button>
                  )}
                </IconTooltip>
              ) : null}
              {contactUsesSocialIcons ? <button type="button" onClick={() => { void handleLogout(); }} aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Sign out', '退出登录')} title={studentText(studentLocale as 'zh-CN' | 'en-US', 'Sign out', '退出登录')} className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]">{shellSettings.logoutIconUrl ? <img src={shellSettings.logoutIconUrl} alt="" className="h-5 w-5 object-contain" /> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5M21 12H9" /></svg>}</button> : null}
            </div>
          ) : null}
          {shellSettings?.showThemeToggle !== false ? <button onClick={toggleTheme}
            className={`flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] ${practiceSidebarCollapsed ? 'justify-center' : ''}`} title={theme === 'dark' ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Light mode', '浅色模式') : studentText(studentLocale as 'zh-CN' | 'en-US', 'Dark mode', '深色模式')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {theme === 'dark'
                ? <>
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </>
                : <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />}
            </svg>
            {!practiceSidebarCollapsed ? (theme === 'dark' ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Light mode', '浅色模式') : studentText(studentLocale as 'zh-CN' | 'en-US', 'Dark mode', '深色模式')) : null}
          </button> : null}
          {!contactUsesSocialIcons && !(isPracticeRoute && shellSettings?.showLogoutInAvatarPopover) ? <button onClick={() => { void handleLogout(); }}
            className={`flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-3 py-2 text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] ${practiceSidebarCollapsed ? 'justify-center' : ''}`} title={studentText(studentLocale as 'zh-CN' | 'en-US', 'Sign out', '退出登录')}>
            {shellSettings?.logoutIconUrl ? <img src={shellSettings.logoutIconUrl} alt="" className="h-[18px] w-[18px] object-contain" /> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>}
            {!practiceSidebarCollapsed ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Sign out', '退出登录') : null}
          </button> : null}
          {shellSettings?.studentFooterCopyright && !practiceSidebarCollapsed ? (
            <p className="px-3 pt-1 text-[11px] leading-5 text-[var(--color-text-tertiary)]">
              {shellSettings.studentFooterCopyright}
            </p>
          ) : null}
        </div>
      </aside>
      {(isCompactSidebarViewport || (isPracticeRoute && shellSettingsFetched && !shellSettingsIsError && practiceSidebarCollapsible)) ? (
        <button
          type="button"
          onClick={() => {
            if (isCompactSidebarViewport) {
              setCompactSidebarOpen((value) => !value);
            } else {
              setPracticeSidebarCollapsedState((value) => !Boolean(value));
            }
          }}
          className={`fixed top-1/2 z-[60] inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-[var(--radius-full)] border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-secondary)] shadow-[var(--shadow-sm)] transition-[left,transform,color,background-color] duration-200 hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-primary)] ${sidebarOpen ? '-translate-x-1/2' : 'translate-x-0'}`}
          style={{ left: sidebarOpen ? 'var(--student-shell-sidebar-panel-width)' : 'var(--student-shell-sidebar-width)' }}
          aria-label={sidebarOpen ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Close navigation sidebar', '关闭导航侧栏') : studentText(studentLocale as 'zh-CN' | 'en-US', 'Open navigation sidebar', '打开导航侧栏')}
          title={sidebarOpen ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Close navigation sidebar', '关闭导航侧栏') : studentText(studentLocale as 'zh-CN' | 'en-US', 'Open navigation sidebar', '打开导航侧栏')}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {sidebarOpen ? <path d="m15 18-6-6 6-6" /> : <path d="m9 18 6-6-6-6" />}
          </svg>
        </button>
      ) : null}
      <main className="min-h-screen min-w-0 flex-1 overflow-x-clip" style={{ marginLeft: 'var(--student-shell-sidebar-width)' }}>
        <Outlet />
      </main>

      {questionNavigationEnabled ? (
        <div
          className={`fixed inset-0 z-[110] transition ${practiceNavigationDrawerOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!practiceNavigationDrawerOpen}
        >
          <div
            className={`absolute inset-0 bg-[color:var(--color-overlay-scrim)] transition-opacity ${practiceNavigationDrawerOpen ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setPracticeNavigationDrawerOpen(false)}
          />
          <aside
            className={`absolute right-0 top-0 h-full w-full max-w-[420px] border-l border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-lg)] transition-transform ${practiceNavigationDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
          >
            <div className="flex h-full flex-col">
              <div className="flex items-start justify-between gap-4 border-b border-[var(--color-border)] px-5 py-5">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">
                    {practiceNavigationDrawerLabel}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-[var(--color-text)]">
                    {studentText(studentLocale as 'zh-CN' | 'en-US', 'Question map', '题目列表')}
                  </div>
                  <div className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    {currentTypeNavigationMeta.totalCount > 0
                      ? `${currentTypeNavigationMeta.currentIndex} / ${currentTypeNavigationMeta.totalCount}`
                      : '0 / 0'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPracticeNavigationDrawerOpen(false)}
                  className="inline-flex h-9 items-center rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)]"
                >
                  {studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden px-4 py-4">
                <PracticeQuestionNavigationTree
                  sections={practiceNavigationDrawerSections}
                  onSelectQuestion={handlePracticeQuestionSelect}
                  compactSingleGroup
                  pageSizePerGroup={40}
                  showColumnHeader
                  layoutVariant="drawer"
                  expansionMode="question"
                  showHighFrequencyBadge={showHighFrequencyNavigationBadge}
                  questionBadgeStyle={shellSettings?.questionBadgeStyle ?? 'soft'}
                  navigationFilterEnabled={practiceNavigationFilterEnabled}
                  navigationFilter={practiceNavigationFilter}
                  navigationFilterShowFrequency={practiceNavigationFilterShowFrequency}
                  navigationFilterShowNew={practiceNavigationFilterShowNew}
                  onNavigationFilterChange={practiceNavigationFilterChange || undefined}
                  onLoadMore={practiceNavigationLoadMore || undefined}
                  loadingMore={practiceNavigationLoadingMore}
                  scrollToCurrentSignal={`${location.pathname}:${practiceNavigationDrawerOpen ? 'open' : 'closed'}`}
                />
              </div>
            </div>
          </aside>
        </div>
      ) : null}

      {questionNavigationEnabled ? (
        <div
          className={`fixed inset-0 z-[120] transition ${sidebarLibraryTypeCode ? 'pointer-events-auto' : 'pointer-events-none'}`}
          aria-hidden={!sidebarLibraryTypeCode}
        >
          <div
            className={`absolute inset-0 bg-[color:var(--color-overlay-scrim)] transition-opacity ${sidebarLibraryTypeCode ? 'opacity-100' : 'opacity-0'}`}
            onClick={() => setSidebarLibraryTypeCode(null)}
          />
          <aside
            className={`absolute bottom-0 right-0 top-0 bg-[var(--color-surface)] shadow-[var(--shadow-lg)] transition-[left,transform] duration-200 ${sidebarLibraryTypeCode ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ left: 'var(--student-shell-sidebar-width)' }}
            role="dialog"
            aria-modal="true"
            aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Question bank', '题库')}
          >
            <div className="flex h-full min-h-0 flex-col">
              <header className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--color-border)] px-5 py-4 sm:px-7">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[var(--color-text)]">
                    {sidebarLibraryGroupMeta?.group.typeName || sidebarLibraryTypeCode}
                  </div>
                  <div className="mt-1 text-xs tabular-nums text-[var(--color-text-secondary)]">
                    {studentText(studentLocale as 'zh-CN' | 'en-US', 'Question bank', '题库')} · {sidebarLibraryItems.length} / {sidebarLibraryTotal}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSidebarLibraryTypeCode(null)}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]"
                  aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Close question bank', '关闭题库')}
                  title={studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
                </button>
              </header>
              <div className="min-h-0 flex-1 overflow-hidden px-4 py-4 sm:px-7 sm:py-6">
                {sidebarLibrarySections.length ? (
                  <PracticeQuestionNavigationTree
                    sections={sidebarLibrarySections}
                    onSelectQuestion={handlePracticeQuestionSelect}
                    compactSingleGroup
                    showColumnHeader
                    layoutVariant="drawer"
                    expansionMode="question"
                    showHighFrequencyBadge={showHighFrequencyNavigationBadge}
                    questionBadgeStyle={shellSettings?.questionBadgeStyle ?? 'soft'}
                    onLoadMore={sidebarLibraryItems.length < sidebarLibraryTotal
                      ? () => {
                          if (!sidebarLibraryFetching) {
                            setSidebarLibraryPage((current) => current + 1);
                          }
                        }
                      : undefined}
                    loadingMore={sidebarLibraryFetching}
                    scrollToCurrentSignal={`${sidebarLibraryTypeCode}:${sidebarLibraryPage}`}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center" role="status">
                    <div className="flex items-center gap-3 text-sm text-[var(--color-text-secondary)]">
                      <span className="h-5 w-5 animate-spin rounded-[var(--radius-full)] border-2 border-[var(--color-border)] border-t-[var(--color-primary)]" aria-hidden="true" />
                      {sidebarLibraryFetching
                        ? studentText(studentLocale as 'zh-CN' | 'en-US', 'Loading questions...', '正在加载题目...')
                        : studentText(studentLocale as 'zh-CN' | 'en-US', 'No questions available', '暂无可用题目')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      ) : null}

      {wechatModalOpen && showContactEntry && shellSettings ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[color:var(--color-overlay-scrim)] p-6">
          <div className="w-full max-w-[420px] rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-6 shadow-[var(--shadow-lg)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">
                  {studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact', '联系方式')}
                </div>
                <div className="mt-1 text-xl font-display font-semibold text-[var(--color-text)]">
                  {shellSettings.contactWechatTitle || studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact support', '联系教务')}
                </div>
              </div>
              {!contactUsesCombinedMode ? (
                <button
                  type="button"
                  onClick={() => setWechatModalOpen(false)}
                  className="inline-flex h-9 items-center rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text-secondary)]"
                >
                  {studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
                </button>
              ) : null}
            </div>

            {shellSettings.contactWechatHint ? (
              <p className="mt-3 text-sm leading-6 text-[var(--color-text-secondary)]">
                {shellSettings.contactWechatHint}
              </p>
            ) : null}

            <div className="mt-5 flex flex-col gap-3">
              {!contactUsesCombinedMode && shellSettings.contactWechatEnabled && shellSettings.contactWechatQrUrl ? (
                <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
                  <img
                    src={shellSettings.contactWechatQrUrl}
                    alt={shellSettings.contactWechatTitle || 'WeChat QR code'}
                    className="mx-auto block h-auto w-full max-w-[280px] rounded-[var(--radius-md)] object-contain"
                  />
                </div>
              ) : null}
              {!contactUsesCombinedMode && shellSettings.contactEmail ? (
                <a
                  href={`mailto:${shellSettings.contactEmail}`}
                  className="inline-flex h-11 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)]"
                >
                  {studentText(studentLocale as 'zh-CN' | 'en-US', 'Email', '邮件')} {shellSettings.contactEmail}
                </a>
              ) : null}
              {shellSettings.contactWechatId ? (
                <button
                  type="button"
                  onClick={() => { void copyContact(); }}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)]"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  {studentText(studentLocale as 'zh-CN' | 'en-US', 'Copy WeChat ID', '复制微信号')}
                </button>
              ) : null}
              {shellSettings.contactWechatAddress ? (
                <a
                  href={shellSettings.contactWechatAddress}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-11 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)]"
                >
                  {studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact via WeChat', '直接微信联系')}
                </a>
              ) : null}
              {!contactUsesCombinedMode && shellSettings.contactExternalUrl ? (
                <a
                  href={shellSettings.contactExternalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-11 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-primary)] px-4 text-sm font-semibold text-[var(--color-text-inverse)] transition-colors hover:bg-[var(--color-primary-hover)]"
                >
                  {shellSettings.contactExternalLabel || studentText(studentLocale as 'zh-CN' | 'en-US', 'Contact support', '联系教务')}
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => setWechatModalOpen(false)}
                className="inline-flex h-11 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)]"
              >
                {studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {customContactDialogOpen && shellSettings?.contactCustomEnabled ? (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-[color:var(--color-overlay-scrim)] p-5"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setCustomContactDialogOpen(false);
            }
          }}
        >
          <section
            className="w-full max-w-[440px] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-5 shadow-[var(--shadow-lg)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-practice-method-title"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase text-[var(--color-text-secondary)]">
                  {shellSettings.contactCustomTooltip || studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice guide', '练习方式')}
                </div>
                <h2 id="custom-practice-method-title" className="mt-1 text-lg font-semibold text-[var(--color-text)]">
                  {shellSettings.contactCustomDialogTitle || shellSettings.contactCustomTooltip || studentText(studentLocale as 'zh-CN' | 'en-US', 'Practice guide', '练习方式')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setCustomContactDialogOpen(false)}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"
                aria-label={studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
                title={studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              </button>
            </div>
            {shellSettings.contactCustomDialogText ? (
              <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-[var(--color-text-secondary)]">
                {shellSettings.contactCustomDialogText}
              </p>
            ) : null}
            <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setCustomContactDialogOpen(false)}
                className="inline-flex h-11 items-center justify-center rounded-[var(--radius-sm)] border border-[var(--color-border)] px-4 text-sm font-semibold text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-muted)]"
              >
                {studentText(studentLocale as 'zh-CN' | 'en-US', 'Close', '关闭')}
              </button>
              {shellSettings.contactCustomDialogButtonUrl ? (
                <a
                  href={shellSettings.contactCustomDialogButtonUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 max-w-full items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-primary)] px-4 py-2 text-center text-sm font-semibold leading-5 text-[var(--color-text-inverse)] transition-colors hover:bg-[var(--color-primary-hover)]"
                  onClick={() => {
                    setCustomContactDialogOpen(false);
                    trackStudentEvent('contact_external_clicked', { properties: { source: 'custom_practice_method_dialog' } });
                  }}
                >
                  {shellSettings.contactCustomDialogButtonLabel || studentText(studentLocale as 'zh-CN' | 'en-US', 'Open', '打开')}
                </a>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </div>
    </StudentLocaleProvider>
  );
}
